/**
 * DOM 기반 HUD — 프롬프트, 피드, 채팅, 인벤토리, 모달(단서/보드/금고).
 */

import type { Net } from './net';
import type { BoardCard, ClueRecord } from '../../shared/protocol';
import { ep } from './episodeData';
import { ep as epData } from './episodeData';
import { renderBook, type BookScript, type BookState, type Memos } from './book';
import './book.css';

export interface HudHooks {
  onChat: (m: string) => void;
  onCode: (propId: string, code: string) => void;
  onDrop: (propId: string) => void;
  onModal: (open: boolean) => void;
  onPair: (playerId: string) => void;
  onMemo: (clueId: string, text: string) => void;
  /** 시신 부위 조사 */
  onBodyPart: (part: string) => void;
  onShot: (clueId: string) => void;
  onSpeaker: (playerId: string) => void;
  onPin: (clueId: string) => void;
  onFlag: (cardId: string) => void;
  onBallot: (playerId: string) => void;
  /** 선택형 조사 — 고른 선택지를 서버에 보낸다 */
  onChoose: (propId: string, option: string) => void;
  onStage: (v: 'investigate' | 'brief' | 'free' | 'vote' | 'result') => void;
}

const IS_TOUCH =
  typeof matchMedia === 'function' &&
  (matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);
const CLOSE_HINT = IS_TOUCH ? '바깥을 탭하거나 ✕ 로 닫기' : '<kbd>Esc</kbd> 닫기';

const ICON_MAG = `<svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5.5 5.5"/></svg>`;
const ICON_ENV = `<svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="13" rx="1.5"/><path d="M3 8l9 6 9-6"/></svg>`;

export class Hud {
  private el: Record<string, HTMLElement> = {};
  private dbg = new URLSearchParams(location.search).has('dbg');
  /** 현장 사진(data URL). main 이 엔진 사진 리그를 꽂아 준다 */
  photoOf: (id: string) => string | null = () => null;
  private toastTimer = 0;
  private codeBuf = '';
  private codeTarget = '';
  private codeDigits = 4;

  constructor(
    root: HTMLElement,
    private net: Net,
    private hooks: HudHooks,
  ) {
    root.insertAdjacentHTML(
      'beforeend',
      `
      <div id="hud">
        <div id="topbar" class="panel">
          <span class="dot" id="netdot"></span>
          <b id="roomname">—</b>
          <span style="color:var(--dim)">|</span>
          <span id="phase">조사</span>
        </div>
        <div id="round"></div>
        <div id="compass"><div id="cstrip"></div><div id="cneedle"></div></div>
        <div id="roster" class="panel"></div>
        <div id="debug" class="panel"></div>
        <div id="crosshair"></div>
        <div id="prompt" class="panel"></div>
        <div id="feed"></div>
        <div id="chatbar"><input type="text" id="chatinput" maxlength="300" placeholder="메시지를 입력하고 Enter" /></div>
        <div id="items"></div>
        <div id="bag" title="촬영해 둔 것"><span class="ic"></span><b id="bagct">0</b></div>
        <div id="toast"></div>
      </div>
      <div id="flash"></div>
      <div id="modal"><div class="sheet panel" id="sheet"></div><button id="sheetclose" aria-label="닫기">✕</button></div>`,
    );

    for (const id of [
      'hud',
      'netdot',
      'roomname',
      'phase',
      'compass',
      'cstrip',
      'round',
      'debug',
      'roster',
      'crosshair',
      'prompt',
      'feed',
      'chatbar',
      'chatinput',
      'items',
      'bag',
      'bagct',
      'toast',
      'flash',
      'modal',
      'sheet',
      'sheetclose',
    ]) {
      this.el[id] = document.getElementById(id)!;
    }

    this.el.bag.onclick = () => this.openBoard();

    const input = this.el.chatinput as HTMLInputElement;
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        const v = input.value.trim();
        input.value = '';
        this.closeChat();
        if (v) this.hooks.onChat(v);
      } else if (e.key === 'Escape') {
        input.value = '';
        this.closeChat();
      }
    });

    this.el.modal.addEventListener('click', (e) => {
      if (e.target === this.el.modal) return this.closeModal();
      // 책의 탭 · 쪽 넘김 · 인물 펼치기
      if (this.el.sheet.dataset.kind === 'cast') {
        const t = (e.target as HTMLElement).closest<HTMLElement>('[data-tab],[data-page],[data-cast-open]');
        if (!t) return;
        if (t.dataset.tab) {
          this.book = { tab: t.dataset.tab as BookState['tab'], page: 0 };
        } else if (t.dataset.page) {
          this.book = { ...this.book, page: Math.max(0, Number(t.dataset.page)) };
        } else {
          this.book = { ...this.book, open: t.dataset.castOpen || undefined };
        }
        return this.renderCast();
      }
    });
    this.el.sheetclose.addEventListener('click', () => this.closeModal());
    this.el.sheet.addEventListener('input', (e) => {
      const t = e.target as HTMLTextAreaElement;
      const id = t.dataset?.memo;
      if (!id) return;
      this.castMemos[id] = t.value;
      try {
        localStorage.setItem(`cs.memo.${this.net.room}`, JSON.stringify(this.castMemos));
      } catch {
        /* 저장이 막혀 있어도 화면은 굴러가야 한다 */
      }
    });

    net.on('log', (m) => this.feed(m, m.startsWith('[조사]') || m.startsWith('[연결]') ? 'clue' : 'sys'));
    net.on('chat', (c) => this.chat(c.name, c.color, c.m));
    net.on('deny', (w) => this.toast(w));
    net.on('players', () => this.refreshRoster());
    net.on('world', () => this.refreshWorld());
    net.on('status', (s) => {
      const dot = this.el.netdot;
      dot.className = 'dot' + (s === 'open' ? '' : s === 'connecting' ? ' warn' : ' bad');
      // **내 연결 상태는 한 줄로만.** 재접속이 반복되면 피드가 같은 문장으로 도배됐다(Chris 09-08)
      if (s === 'closed') this.sticky('연결이 끊겼습니다. 재접속 중…');
      else if (s === 'open') this.sticky(null);
    });
  }

  show() {
    this.el.hud.classList.add('on');
    this.el.roomname.textContent = `ROOM ${this.net.room.toUpperCase()}`;
  }

  // --- 프롬프트 ---

  setPrompt(html: string | null) {
    const p = this.el.prompt;
    if (!html) {
      p.classList.remove('on');
      this.el.crosshair.classList.remove('hot');
      return;
    }
    p.innerHTML = html;
    p.classList.add('on');
    this.el.crosshair.classList.add('hot');
  }

  // --- 피드 / 채팅 ---

  /**
   * **피드는 잠깐 보이고 사라진다.** 로그가 쌓여 화면 왼쪽 아래를 차지했다(Chris 09-08).
   * 입장·배역 안내 같은 것은 안 띄우고, 나갔다·끊겼다·조사·감정만 띄운다. 같은 줄이 연달아 오면 한 번만.
   */
  private lastFeed = '';
  private stickyEl: HTMLDivElement | null = null;
  feed(text: string, cls: 'sys' | 'clue' | '' = '', ttl = 6000) {
    if (/입장했습니다|배역을 고르세요|화면을 클릭하면/.test(text)) return;
    if (text === this.lastFeed) return;
    this.lastFeed = text;
    const d = document.createElement('div');
    d.className = cls;
    d.textContent = text;
    this.el.feed.appendChild(d);
    while (this.el.feed.children.length > 5) this.el.feed.removeChild(this.el.feed.firstChild!);
    window.setTimeout(() => { d.classList.add('out'); window.setTimeout(() => d.remove(), 400); }, ttl);
  }

  /** 계속 떠 있어야 하는 한 줄(내 연결 끊김). null 이면 지운다 */
  private sticky(text: string | null) {
    if (this.stickyEl) { this.stickyEl.remove(); this.stickyEl = null; }
    if (!text) return;
    const d = document.createElement('div');
    d.className = 'sys sticky';
    d.textContent = text;
    this.el.feed.appendChild(d);
    this.stickyEl = d;
  }

  chat(name: string, color: string, m: string) {
    const d = document.createElement('div');
    d.className = 'chat';
    d.innerHTML = `<b style="color:${escapeAttr(color)}">${escapeHtml(name)}</b> ${escapeHtml(m)}`;
    this.el.feed.appendChild(d);
    while (this.el.feed.children.length > 5) this.el.feed.removeChild(this.el.feed.firstChild!);
    window.setTimeout(() => { d.classList.add('out'); window.setTimeout(() => d.remove(), 400); }, 12000);
  }

  openChat() {
    this.el.chatbar.classList.add('on');
    (this.el.chatinput as HTMLInputElement).focus();
    // 모바일 키보드가 올라오면 입력창이 가려진다 — 보이는 영역 위로 띄운다
    const vv = window.visualViewport;
    if (vv) {
      const follow = () => {
        const hidden = window.innerHeight - vv.height - vv.offsetTop;
        this.el.chatbar.style.bottom = `${Math.max(0, hidden) + 12}px`;
      };
      follow();
      vv.addEventListener('resize', follow);
      vv.addEventListener('scroll', follow);
      this.detachViewport = () => {
        vv.removeEventListener('resize', follow);
        vv.removeEventListener('scroll', follow);
        this.el.chatbar.style.bottom = '';
      };
    }
  }

  private detachViewport: (() => void) | null = null;

  closeChat() {
    this.el.chatbar.classList.remove('on');
    (this.el.chatinput as HTMLInputElement).blur();
    this.detachViewport?.();
    this.detachViewport = null;
  }

  get chatOpen() {
    return this.el.chatbar.classList.contains('on');
  }

  toast(msg: string) {
    this.el.toast.textContent = msg;
    this.el.toast.classList.add('on');
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.el.toast.classList.remove('on'), 2200);
  }

  // --- 상태 표시 ---

  /** 명단 — 칩 한 줄. 호스트는 왕관, 나는 밑줄. 「HOST」 글자와 「(나)」가 자리를 먹었다 */
  refreshRoster() {
    const rows = [...this.net.players.values()]
      .map(
        (p) =>
          `<span class="chip${p.id === this.net.you ? ' me' : ''}" title="${escapeAttr(p.name)}${p.host ? ' · 호스트' : ''}${p.id === this.net.you ? ' · 나' : ''}">` +
          `<span class="sw" style="background:${escapeAttr(p.color)}"></span>${p.host ? '<span class="crown">♛</span>' : ''}${escapeHtml(p.name)}</span>`,
      )
      .join('');
    this.el.roster.innerHTML = rows || '<span class="chip">—</span>';
  }

  /** 라운드 상태 한 줄 — 위 왼쪽 방 이름 아래. 디버그 패널에 섞여 있던 것을 뺐다 */
  setRound(text: string) {
    this.el.round.textContent = text;
    this.el.round.classList.toggle('on', !!text);
  }

  refreshWorld() {
    const phaseName: Record<string, string> = {
      investigate: '조사',
      discuss: '토론',
      vote: '투표',
      result: '결과',
    };
    /* 「내가 가진 것 / 판에서 나온 것」. 전에는 전원 합계 하나만 떠서
       남이 뭘 찾았는지가 숫자로 새어 나갔다 */
    this.el.phase.textContent = `${phaseName[this.net.world.phase] ?? this.net.world.phase} · 단서 ${
      Object.keys(this.net.world.clues).length
    }/${Object.keys(this.net.world.taken ?? {}).length}`;

    const shots = Object.keys(this.net.world.shots ?? {}).length;
    this.el.bagct.textContent = String(shots);
    this.el.bag.classList.toggle('empty', shots === 0);

    const items = this.net.myItems();
    this.el.items.innerHTML = items
      .map(
        (id) =>
          `<div class="item" data-drop="${escapeAttr(id)}">${escapeHtml(
            ep().PROP_BY_ID[id]?.name ?? id,
          )}<small>G 놓기</small></div>`,
      )
      .join('');
    for (const node of this.el.items.querySelectorAll<HTMLElement>('[data-drop]')) {
      node.onclick = () => this.hooks.onDrop(node.dataset.drop!);
    }

    // 열려 있는 보드/단서 모달은 즉시 갱신
    if (this.el.modal.classList.contains('on') && this.el.sheet.dataset.kind === 'board') this.openBoard();
    if (this.el.modal.classList.contains('on') && this.el.sheet.dataset.kind === 'squads') this.openSquads();
  }

  /** 성능·좌표 — `?dbg=1` 일 때만 보인다 */
  setDebug(lines: string[]) {
    if (!this.dbg) { this.el.debug.style.display = 'none'; return; }
    this.el.debug.innerHTML = lines.join('<br>');
  }

  // --- 나침반 ---
  //
  // **왜 필요한가**: 여섯 명이 같은 맵을 돌아다니며 「저쪽 창문 봐」라고 말하는 게임이다.
  // 방향을 가리킬 공통 언어가 없으면 서로 다른 벽을 본다.
  //
  // 방위 규약은 레벨이 정한다 — ROOMS 주석이 z -20~-10 을 「안길 북쪽」, z 10~24 를
  // 「안길 남쪽」이라 부르므로 **-Z 가 북, +Z 가 남, -X 가 서, +X 가 동**이다.
  //
  // yaw → 방위: 카메라는 `rotateY(yaw)` 로 돌고 three 의 기본 시선은 -Z 이므로 시선
  // 벡터가 (-sin yaw, 0, -cos yaw) 다. yaw 0 이 -Z(북), yaw 가 커지면 북→서→남→동으로
  // **반시계**로 돈다. 그래서 방위각은 yaw 를 뒤집은 값이다.

  /** 스트립 픽셀/도 — 창이 240px 이므로 60도 시야가 보인다 */
  private static readonly CPPD = 4;
  private compassReady = false;

  private buildCompass() {
    const marks: string[] = [];
    const NAMES: [number, string][] = [
      [0, '북'], [45, '북동'], [90, '동'], [135, '남동'],
      [180, '남'], [225, '남서'], [270, '서'], [315, '북서'],
    ];
    // 두 바퀴를 이어 붙인다 — 0/360 경계에서 라벨이 끊기지 않게
    for (let turn = 0; turn < 2; turn++) {
      for (const [deg, name] of NAMES) {
        const x = (deg + turn * 360) * Hud.CPPD;
        marks.push(
          `<span class="cl${deg % 90 === 0 ? ' maj' : ''}" style="left:${x}px">${name}</span>`,
        );
      }
    }
    this.el.cstrip.innerHTML = marks.join('');
    this.compassReady = true;
  }

  /** 매 프레임 호출한다. yaw 만 있으면 된다 */
  setCompass(yaw: number) {
    if (!this.compassReady) this.buildCompass();
    // yaw 를 뒤집어 방위각(북=0, 동=90, 남=180, 서=270)으로
    const bearing = (((-yaw * 180) / Math.PI) % 360 + 360) % 360;
    // 스트립의 두 번째 바퀴를 기준으로 잡는다(양쪽에 한 바퀴씩 여유가 있다)
    const px = -(bearing + 360) * Hud.CPPD + 120;
    this.el.cstrip.style.transform = `translateX(${px}px)`;
  }

  // --- 모달 ---

  get modalOpen() {
    return this.el.modal.classList.contains('on');
  }

  /** 책 상태 — 어느 탭, 몇 쪽, 누구를 펼쳤나 */
  private book: BookState = { tab: 'script', page: 0 };
  private myScript: BookScript | null = null;
  private castMemos: Memos = {};

  private open(kind: string, html: string) {
    this.el.sheet.dataset.kind = kind;
    this.el.sheet.innerHTML = html;
    this.el.modal.classList.add('on');
    document.body.classList.add('modal-open');
    this.hooks.onModal(true);
  }

  closeModal() {
    if (!this.modalOpen) return;
    // 모달 안의 영상도 같이 멈춘다 — 닫힌 뒤에도 소리가 나면 안 된다
    this.mediaEl?.pause();
    this.mediaEl = null;
    this.el.modal.classList.remove('on');
    document.body.classList.remove('modal-open');
    this.hooks.onModal(false);
  }

  /**
   * 단서 한 장.
   *
   * 밑에 열 글자 메모를 쓸 수 있다. **발표할 때 기억하려고** 적는 것이라
   * 안 써도 된다 — 안 쓰면 칠판에 단서 이름이 그대로 걸린다.
   *
   * 다만 쓰면 그게 **주장**이 된다. 칠판에 걸리는 건 원문이 아니라 이 열 글자라서,
   * 「폰 새벽발신」이라고 쓰는 순간 그 사람의 해석이 판에 걸린다.
   */
  openClue(c: ClueRecord, memo = '') {
    /* 몇 번째로 나온 단서인가. 카드에 번호가 박히면 모으는 물건이 된다 */
    const order = Object.values(this.net.world.clues)
      .sort((a, b) => a.at - b.at)
      .findIndex((x) => x.id === c.id);
    const no = String(order < 0 ? Object.keys(this.net.world.clues).length : order + 1).padStart(2, '0');

    const shot = !!this.net.world.shots?.[c.id];
    const ph = this.photoOf(c.id);
    const pic = ph
      ? `<figure class="pic photo"><img src="${ph}" alt=""></figure>`
      : c.img
        ? `<figure class="pic"><img src="/clues/${encodeURIComponent(c.img)}" alt=""></figure>`
        : '';
    const fx = c.forensic
      ? `<div class="forensic">${this.net.world.reports?.[c.forensic]
          ? '감정서가 도착했습니다'
          : '감정 대상 — 칠판에 걸면 감정서가 옵니다'}</div>`
      : '';

    this.open(
      'clue',
      `<article class="cluecard${shot ? ' shot' : ''}">
         <span class="no">${no}</span>
         <h2>${escapeHtml(c.title)}</h2>
         ${pic}
         <div class="text">${escapeHtml(c.text)}</div>
         ${fx}
         <div class="memo">
           <label for="memoin">칠판에 걸 열 글자 <em>선택</em></label>
           <div class="row">
             <input id="memoin" maxlength="10" placeholder="안 쓰면 단서 이름이 그대로 걸립니다"
                    value="${escapeHtml(memo)}">
             <span id="memoct">${memo.length}/10</span>
           </div>
         </div>
         <div class="shotbar">
           <button id="shotbtn" ${shot ? 'disabled' : ''}>
             ${shot ? '가방에 있음' : '촬영하기'}
           </button>
           <span class="why">찍어 둔 것만 나중에 칠판에 걸 수 있습니다</span>
         </div>
         <footer>
           <span></span>
           <span class="secret">칠판에 걸지 않으면 본인만 볼 수 있습니다</span>
         </footer>
       </article>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    const inp = document.getElementById('memoin') as HTMLInputElement | null;
    const ct = document.getElementById('memoct');
    if (inp) {
      inp.oninput = () => {
        if (ct) ct.textContent = `${inp.value.length}/10`;
        this.hooks.onMemo(c.id, inp.value);
      };
      // 자동 포커스하지 않는다 — 포커스가 가면 P(촬영)가 메모에 글자로 들어간다.
      // 메모는 쓰고 싶은 사람이 클릭해서 쓴다
    }

    const btn = document.getElementById('shotbtn') as HTMLButtonElement | null;
    if (btn && !shot) {
      btn.onclick = () => {
        btn.disabled = true;
        btn.textContent = '가방에 있음';
        this.hooks.onShot(c.id);
        this.shootAnim();
      };
    }
  }

  /** 단서 카드가 열려 있으면 그것을 찍는다(P 키). 찍을 게 없으면 false */
  shootOpen(): boolean {
    if (!this.modalOpen || this.el.sheet.dataset.kind !== 'clue') return false;
    const btn = document.getElementById('shotbtn') as HTMLButtonElement | null;
    if (btn && !btn.disabled) btn.click();
    else this.toast('이미 찍어 둔 것입니다.');
    return true;
  }

  /** 카드 없이 조준한 채 찍을 때의 플래시 */
  flash() {
    const f = document.createElement('div');
    f.className = 'shotflash';
    document.body.appendChild(f);
    setTimeout(() => f.remove(), 260);
  }

  /**
   * 촬영 연출 — 약한 플래시 한 번, 그리고 카드가 가방으로 빨려 들어간다.
   *
   * **세게 터뜨리지 않는다.** 어두운 방을 두 시간 보는 게임이라 흰 화면이 세게
   * 터지면 눈이 아프고, 무엇보다 여러 번 반복되는 동작이다.
   */
  private shootAnim() {
    const card = this.el.sheet.querySelector<HTMLElement>('.cluecard');
    const bag = this.el.bag;
    if (!card || !bag) return this.closeModal();

    this.el.flash.classList.add('on');
    setTimeout(() => this.el.flash.classList.remove('on'), 190);

    /* 카드 중심 → 가방 중심으로 옮겨 앉히는 변환을 직접 계산한다.
       CSS 만으로는 가방 위치가 화면 크기마다 달라 못 맞춘다 */
    const a = card.getBoundingClientRect();
    const b = bag.getBoundingClientRect();
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const s = Math.max(0.06, b.width / a.width);

    card.style.transition = 'transform 460ms cubic-bezier(0.55,0,0.85,0.35), opacity 460ms ease-in';
    card.style.transformOrigin = '50% 50%';
    requestAnimationFrame(() => {
      card.style.transform = `translate(${dx}px, ${dy}px) scale(${s}) rotate(9deg)`;
      card.style.opacity = '0.15';
    });

    setTimeout(() => {
      bag.classList.add('bump');
      setTimeout(() => bag.classList.remove('bump'), 320);
      this.closeModal();
    }, 430);
  }

  /**
   * 진술 · 난상 추리 · 지목 · 결과.
   *
   * 배치의 원칙 하나 — **말하는 사람이 제일 크다.** 거짓말은 카드가 아니라
   * 태도에서 걸린다. 칠판은 옆이다.
   * 캐릭터 이미지는 나중에 들어온다. 지금은 자리만 비워 둔다.
   */
  openBrief(myClues: ClueRecord[], memos: Record<string, string>) {
    const w = this.net.world;
    const me = this.net.you ?? '';
    const nameOf = (id: string) => {
      const cid = w.roles[id];
      return cid ? epData().cast.byId[cid]?.name ?? '?' : this.net.players.get(id)?.name ?? '?';
    };
    const isDet = epData().cast.byId[w.roles[me] ?? '']?.role === 'detective';
    const iSpeak = w.speaker === me;
    const sec = Math.max(0, Math.round((w.stageEndsAt - Date.now()) / 1000));

    const stageName: Record<string, string> = {
      investigate: '조사', brief: '진술', free: '난상 추리', vote: '지목', result: '결과',
    };

    /* 발표자 — 제일 크다 */
    const stage = w.speaker
      ? `<div class="podium">
           <div class="portrait"><span>${escapeHtml(nameOf(w.speaker))}</span></div>
           <div class="who">
             <b>${escapeHtml(nameOf(w.speaker))}</b> 진술 중
             <span class="t">${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}</span>
             <div class="asked dim">말은 누구나 언제든 합니다. 발표자는 칠판에 카드를 겁니다</div>
           </div>
         </div>`
      : `<div class="podium empty">${w.phase === 'brief' ? '탐정이 다음 사람을 부릅니다' : stageName[w.phase]}</div>`;

    /* 칠판 — 걸린 카드는 사진이 보이고, 누르면 원문이 열린다.
       전에는 이름과 열 글자만 있어서 「사진을 들이댄다」가 불가능했다 */
    const board = w.board.length
      ? w.board
          .map((c) => {
            const flags = (w.flags[c.id] ?? []).length;
            const pic = c.img
              ? `<img src="/clues/${encodeURIComponent(c.img)}" alt="">`
              : '<span class="nopic">사진 없음</span>';
            return `<div class="card">
                <button class="pic" data-open="${c.id}" title="눌러서 크게 보기">${pic}</button>
                <div class="memo">${escapeHtml(c.memo)}</div>
                <div class="foot">
                  <span>${escapeHtml(nameOf(c.by))}</span>
                  <button class="fl ${flags ? 'on' : ''}" data-flag="${c.id}">이상하다${
                    flags ? ` ${flags}` : ''
                  }</button>
                </div>
              </div>`;
          })
          .join('')
      : '<div class="empty">아직 걸린 카드가 없습니다.</div>';

    /* 사람들 */
    const people = [...this.net.players.keys()]
      .map((id) => {
        const on = id === w.speaker;
        const done = w.briefDone.includes(id);
        const voted = w.votes[id];
        return `<button class="seat ${on ? 'on' : ''} ${done ? 'done' : ''}" data-pick="${id}">
            ${escapeHtml(nameOf(id))}
            ${done ? '<b>발표함</b>' : ''}${on ? '<b>발표 중</b>' : ''}
            ${w.phase === 'vote' && voted ? '<b>투표함</b>' : ''}
          </button>`;
      })
      .join('');

    /* 내 단서 가방 */
    const bag = myClues.length
      ? myClues
          .map((c) => {
            const m = memos[c.id] ?? '';
            const pinned = w.board.some((b) => b.clueId === c.id);
            return `<button class="bagitem" data-pin="${c.id}" ${pinned ? 'disabled' : ''}>
                <span>${escapeHtml(c.title)}</span>
                <b>${pinned ? '걸림' : m ? escapeHtml(m) : '메모 없음'}</b>
              </button>`;
          })
          .join('')
      : '<div class="empty">아직 찾은 단서가 없습니다.</div>';

    const ctl: string[] = [];
    if (isDet && w.phase === 'brief') ctl.push('<div class="meta">아래에서 사람을 눌러 진술자를 부릅니다.</div>');
    if (iSpeak) ctl.push('<div class="meta">가방에서 카드를 눌러 칠판에 겁니다. 한 차례 3장.</div>');
    if (w.phase === 'vote') ctl.push('<div class="meta">아래에서 지목합니다. 탐정은 두 표입니다.</div>');
    if (this.net.isHost()) {
      ctl.push(
        `<div class="stages">${(['brief', 'free', 'vote', 'result'] as const)
          .map((v) => `<button data-stage="${v}" ${w.phase === v ? 'disabled' : ''}>${stageName[v]}</button>`)
          .join('')}</div>`,
      );
    }

    this.open(
      'brief',
      `<h2>${stageName[w.phase] ?? w.phase}</h2>
       ${stage}
       <h3>칠판</h3>
       <div class="boardgrid">${board}</div>
       <h3>사람${isDet && w.phase === 'brief' ? ' — 눌러서 발표자를 부릅니다' : ''}</h3>
       <div class="seats">${people}</div>
       <h3>단서 가방</h3>
       <div class="bag">${bag}</div>
       ${ctl.join('')}
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );

    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-pick]')) {
      b.onclick = () => {
        const id = b.dataset.pick!;
        if (w.phase === 'vote') this.hooks.onBallot(id);
        else if (isDet && w.phase === 'brief') this.hooks.onSpeaker(id);
        setTimeout(() => this.openBrief(myClues, memos), 150);
      };
    }
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-pin]')) {
      b.onclick = () => {
        this.hooks.onPin(b.dataset.pin!);
        setTimeout(() => this.openBrief(myClues, memos), 200);
      };
    }
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-flag]')) {
      b.onclick = () => {
        this.hooks.onFlag(b.dataset.flag!);
        setTimeout(() => this.openBrief(myClues, memos), 150);
      };
    }
    /* 걸린 카드를 크게 본다. 회의 중에 사진을 들이대는 자리다 */
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-open]')) {
      b.onclick = () => {
        const card = w.board.find((x) => x.id === b.dataset.open);
        if (!card) return;
        this.openPinned(card, nameOf(card.by), () => this.openBrief(myClues, memos));
      };
    }
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-stage]')) {
      b.onclick = () => this.hooks.onStage(b.dataset.stage as 'brief');
    }
  }

  /**
   * 칠판에 걸린 카드를 크게 본다.
   *
   * 조사할 때 뜨는 카드와 같은 모양이되, **촬영 버튼과 메모 칸이 없다** —
   * 이미 공개된 것이라 고를 것이 없다. 대신 「누가 걸었나」가 제목 옆에 붙는다.
   */
  private openPinned(c: BoardCard, byName: string, back: () => void) {
    const pic = c.img
      ? `<figure class="pic"><img src="/clues/${encodeURIComponent(c.img)}" alt=""></figure>`
      : '';
    this.open(
      'clue',
      `<article class="cluecard pinned">
         <h2>${escapeHtml(c.title)}</h2>
         ${pic}
         <div class="text">${escapeHtml(c.text)}</div>
         <footer>
           <span>${escapeHtml(byName)} 이(가) 칠판에 걸었습니다 — 「${escapeHtml(c.memo)}」</span>
           <span>전원 공개</span>
         </footer>
       </article>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    /* 닫으면 토론 화면으로 돌아간다. 안 그러면 회의 중에 판이 끊긴다 */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      document.removeEventListener('keydown', onKey, true);
      setTimeout(back, 0);
    };
    document.addEventListener('keydown', onKey, true);
    this.el.modal.addEventListener(
      'click',
      (e) => {
        if (e.target !== this.el.modal) return;
        document.removeEventListener('keydown', onKey, true);
        setTimeout(back, 0);
      },
      { once: true },
    );
  }

  /**
   * **수첩 = 폴라로이드.** 찍은 것은 사진으로, 본 것만 있는 건 회색 한 줄로.
   * 감정 대상에는 붉은 돋보기 도장, 감정서가 오면 봉투로 바뀐다 (Chris 2026-09-07).
   * 사진은 조사한 순간 엔진이 찍은 것이고, 없으면 카탈로그 그림, 그것도 없으면 제목만.
   */
  openBoard() {
    const clues = Object.values(this.net.world.clues).sort((a, b) => a.at - b.at);
    const shots = this.net.world.shots ?? {};
    const reports = this.net.world.reports ?? {};
    const taken = Object.values(clues).filter((c) => shots[c.id]);
    const seen = clues.filter((c) => !shots[c.id]);
    const stamp = (c: ClueRecord) =>
      !c.forensic ? '' : reports[c.forensic]
        ? `<span class="stamp env" title="감정서 도착">${ICON_ENV}</span>`
        : `<span class="stamp" title="감정 대상">${ICON_MAG}</span>`;
    const pol = (c: ClueRecord, i: number) => {
      const ph = this.photoOf(c.id) ?? (c.img ? `/clues/${encodeURIComponent(c.img)}` : null);
      const img = ph ? `<img src="${ph}" alt="">` : `<div class="noimg">${escapeHtml(c.title)}</div>`;
      return `<div class="pol" data-id="${c.id}" style="--tilt:${((i * 7) % 5) - 2}deg">${img}
        <div class="cap">${escapeHtml(c.title)}</div>${stamp(c)}</div>`;
    };
    const grid = taken.length
      ? `<div class="pol-grid">${taken.map(pol).join('')}</div>`
      : '<div class="empty">찍어 둔 사진이 없습니다. 조사한 것을 보며 P 를 누르세요.</div>';
    const list = seen.length
      ? `<div class="seen"><h3>본 것 — 아직 안 찍음</h3>${seen
          .map((c) => `<div class="row" data-id="${c.id}">${escapeHtml(c.title)}${c.forensic ? ' <em>감정 대상</em>' : ''}</div>`)
          .join('')}</div>`
      : '';
    const all = Object.keys(this.net.world.taken ?? {}).length;
    this.open(
      'board',
      `<h2>내 수첩</h2>
       <div class="meta">여기 있는 것은 <b>나만 봅니다.</b> 찍은 사진만 진술 차례에 칠판에 걸 수 있습니다
         · 사진 ${taken.length} / 본 것 ${clues.length} / 판에서 나온 것 ${all}</div>
       ${grid}${list}
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    this.el.sheet.querySelectorAll<HTMLElement>('[data-id]').forEach((el) => {
      el.onclick = () => {
        const c = this.net.world.clues[el.dataset.id!];
        if (c) this.openClue(c, this.memoOf?.(c.id) ?? '');
      };
    });
  }
  /** main 이 꽂아 주는 메모 조회 (없으면 빈 메모) */
  memoOf?: (id: string) => string;

  /**
   * **시신 부위 조사.** 크라임씬처럼 시신을 누르면 실루엣이 뜨고 부위마다 눌러 본다.
   * 단서 없는 부위도 눌러 본다 — 「특이사항 없음」이 정보다. 「옷 걷기」를 누르면 안쪽 부위가 열린다.
   * 결과는 서버가 부위별로 준다(`bodypart`). 단서가 있는 부위는 수첩·촬영·감정 도장이 그대로 붙는다.
   */
  private bodyState: Record<string, { text: string; clueId?: string }> = {};
  private bodyUnder = false;
  private bodyParts: { id: string; label: string; under?: boolean; clue?: string }[] = [];
  openBody(parts: { id: string; label: string; under?: boolean; clue?: string }[]) {
    this.bodyParts = parts;
    this.renderBody();
  }
  bodyResult(r: { part: string; text: string; clueId?: string }) {
    this.bodyState[r.part] = { text: r.text, clueId: r.clueId };
    if (this.modalOpen && this.el.sheet.dataset.kind === 'body') this.renderBody(r.part);
  }
  private renderBody(focus?: string) {
    // 실루엣 좌표 (viewBox 0 0 200 420) — 앞모습. under 부위는 옷 걷기 때만
    const R: Record<string, string> = {
      head: '<circle cx="100" cy="38" r="26"/>', face: '<ellipse cx="100" cy="44" rx="14" ry="12"/>',
      neck: '<rect x="88" y="64" width="24" height="18" rx="4"/>',
      chest: '<rect x="68" y="84" width="64" height="66" rx="8"/>', chest_skin: '<rect x="74" y="90" width="52" height="54" rx="6"/>',
      pocket_top: '<rect x="76" y="126" width="18" height="14" rx="2"/>',
      belly: '<rect x="72" y="152" width="56" height="40" rx="6"/>', back: '<rect x="140" y="96" width="44" height="90" rx="6"/>',
      arm_l: '<rect x="36" y="88" width="26" height="110" rx="10"/>', arm_r: '<rect x="138" y="88" width="26" height="110" rx="10"/>',
      hand_l: '<circle cx="49" cy="214" r="14"/>', hand_r: '<circle cx="151" cy="214" r="14"/>',
      pocket_pants: '<rect x="76" y="198" width="16" height="16" rx="2"/>',
      legs: '<rect x="72" y="194" width="56" height="150" rx="10"/>', feet: '<rect x="70" y="346" width="60" height="22" rx="6"/>',
    };
    const st = this.bodyState;
    const shapes = this.bodyParts
      .filter((p) => !p.under || this.bodyUnder)
      .map((p) => {
        const s = st[p.id];
        const cls = ['part', s ? 'seen' : '', s?.clueId ? 'clue' : '', p.id === focus ? 'focus' : ''].filter(Boolean).join(' ');
        return `<g class="${cls}" data-part="${p.id}"><title>${escapeHtml(p.label)}</title>${R[p.id] ?? ''}</g>`;
      })
      .join('');
    const rows = this.bodyParts
      .filter((p) => !p.under || this.bodyUnder)
      .map((p) => {
        const s = st[p.id];
        return `<div class="brow ${s ? 'seen' : ''} ${s?.clueId ? 'clue' : ''} ${p.id === focus ? 'focus' : ''}" data-part="${p.id}">
          <b>${escapeHtml(p.label)}</b><span>${s ? escapeHtml(s.text) : '아직 안 봄'}</span>${s?.clueId ? '<em>단서</em>' : ''}</div>`;
      })
      .join('');
    const seen = Object.keys(st).length, total = this.bodyParts.length;
    this.open(
      'body',
      `<h2>시신 조사 <small>${seen}/${total} 부위</small></h2>
       <div class="body-wrap">
         <div class="body-fig">
           <svg viewBox="0 0 200 420" xmlns="http://www.w3.org/2000/svg">
             <path class="sil" d="M100 12c-14 0-26 12-26 26 0 10 5 19 12 24v8c-14 4-26 12-30 26l-14 90c-2 8 6 12 10 6l16-58v70l-8 130c-1 8 10 10 12 2l14-100 14 100c2 8 13 6 12-2l-8-130v-70l16 58c4 6 12 2 10-6l-14-90c-4-14-16-22-30-26v-8c7-5 12-14 12-24 0-14-12-26-26-26z"/>
             ${shapes}
           </svg>
           <button id="bodyunder" class="under ${this.bodyUnder ? 'on' : ''}">${this.bodyUnder ? '옷 내리기' : '옷 걷기'}</button>
         </div>
         <div class="body-list">${rows}</div>
       </div>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    this.el.sheet.querySelectorAll<HTMLElement>('[data-part]').forEach((el) => {
      el.onclick = () => this.hooks.onBodyPart(el.dataset.part!);
    });
    const ub = document.getElementById('bodyunder');
    if (ub) ub.onclick = () => { this.bodyUnder = !this.bodyUnder; this.renderBody(); };
  }

  /**
   * 조 편성. 크라임씬 1차 조사는 한 조씩 나갔다 온다.
   * 서로를 지목해야 조가 된다 — 한쪽만 찍으면 「신청」에 머문다.
   */
  /**
   * 인물.
   *
   * **두 해상도로 나뉜다.** 남에 대해서는 「공개 정보」와 「그 모습에서 알 수
   * 있는 것」까지만 본다 — 손목에 시계 자국만 있고 시계가 없다, 왼손 검지 끝
   * 마디가 없다. 왜 그런지는 그 사람 대본에만 있다.
   * 내 것은 서버가 나에게만 보내 준 대본 전체를 편다.
   */
  /**
   * **인물과 대본은 책 한 권이다.**
   *
   * 여기에 검은 목록이 따로 있었고 대기실에는 양피지 책이 따로 있었다.
   * 같은 것을 두 벌 만들어 두고 게임 시작할 때 옛것을 띄우고 있었다 —
   * 「이건 뭐야 중복임?」이 나온 이유다. 대기실과 같은 책을 편다.
   */
  openCast(mine: BookScript | null) {
    if (mine) this.myScript = mine;
    this.renderCast();
  }

  private renderCast() {
    const playerOfCast: Record<string, string> = {};
    for (const [pid, cid] of Object.entries(this.net.world.roles)) {
      playerOfCast[cid] = this.net.players.get(pid)?.name ?? '?';
    }
    this.open(
      'cast',
      renderBook(this.book, this.myScript, playerOfCast, [], this.castMemos) +
        `<div class="close-hint">${CLOSE_HINT}</div>`,
    );
  }

  /** 결과. 표와 진실이 한 번에 나온다 */
  openVerdict(v: {
    accused: string;
    culpritCast: string;
    caught: boolean;
    tally: { id: string; n: number }[];
    truth: string;
  }) {
    const nameOf = (id: string) => {
      const cid = this.net.world.roles[id];
      return cid ? epData().cast.byId[cid]?.name ?? '?' : this.net.players.get(id)?.name ?? '?';
    };
    const culprit = epData().cast.byId[v.culpritCast]?.name ?? '?';
    const rows = v.tally
      .map((t) => `<div class="row">${escapeHtml(nameOf(t.id))} — ${t.n}표</div>`)
      .join('');
    this.open(
      'verdict',
      `<h2>${v.caught ? '검거' : '검거 실패'}</h2>
       <div class="meta">${v.accused ? `${escapeHtml(nameOf(v.accused))} 님이 지목됐습니다.` : '아무도 지목되지 않았습니다.'}
         진범은 <b>${escapeHtml(culprit)}</b> 입니다.</div>
       ${rows}
       <h3 style="margin-top:18px">사건의 전말</h3>
       <p style="white-space:pre-wrap;line-height:1.75">${escapeHtml(v.truth)}</p>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
  }

  openSquads() {
    const me = this.net.you ?? '';
    const w = this.net.world;
    const outNames = w.out.map((id) => this.net.players.get(id)?.name ?? '?');
    const left = Math.max(0, Math.round((w.turnEndsAt - Date.now()) / 1000));

    const rows = [...this.net.players.values()]
      .filter((p) => p.id !== me)
      .map((p) => {
        const gone = w.done.includes(p.id);
        const iPicked = w.pairing[me] === p.id;
        const theyPicked = w.pairing[p.id] === me;
        const state = gone
          ? '다녀옴'
          : w.out.includes(p.id)
            ? '나가 있음'
            : iPicked && theyPicked
              ? '한 조'
              : iPicked
                ? '내가 신청'
                : theyPicked
                  ? '나를 지목'
                  : '';
        return `<button class="pick" data-pair="${p.id}" ${gone ? 'disabled' : ''}>
            <span class="dot" style="background:${escapeHtml(p.color)}"></span>
            ${escapeHtml(p.name)}
            <b>${state}</b>
          </button>`;
      })
      .join('');

    const now = w.out.length
      ? `<div class="row"><b>${escapeHtml(outNames.join(' · '))}</b> 나가 있음 — ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} 남음</div>`
      : '<div class="row">아무도 안 나가 있습니다. 서로 지목하면 그 둘이 바로 나갑니다.</div>';
    const doneRow = w.done.length
      ? `<div class="row">다녀온 사람 — ${escapeHtml(w.done.map((id) => this.net.players.get(id)?.name ?? '?').join(' · '))}</div>`
      : '';

    this.open(
      'squads',
      `<h2>누구랑 나갈까</h2>
       <div class="meta">한 조씩 나갔다 옵니다. <b>서로</b> 골라야 그 둘이 나갑니다.<br>
         조는 미리 다 짜는 게 아니라, 앞 조가 돌아온 다음에 남은 사람끼리 그때 정합니다.</div>
       ${now}
       ${doneRow}
       <div class="pick-grid" style="margin-top:12px">${rows || '<div class="empty">아직 다른 참가자가 없습니다.</div>'}</div>
       <div class="close-hint"><kbd>F7</kbd> 일찍 돌아가기 · ${CLOSE_HINT}</div>`,
    );
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-pair]')) {
      b.onclick = () => {
        const id = b.dataset.pair!;
        this.hooks.onPair(this.net.world.pairing[this.net.you ?? ''] === id ? '' : id);
        setTimeout(() => this.openSquads(), 150);
      };
    }
  }

  openKeypad(propId: string, title = '금고', hint = '', digits = 4) {
    this.codeTarget = propId;
    this.codeBuf = '';
    this.codeDigits = digits;   // 6자리 잠금도 있다 — 노트북 PIN·타임코드 (사흘 v8)
    this.open(
      'keypad',
      `<h2>${escapeHtml(title)}</h2>
       <div class="meta">${hint ? escapeHtml(hint) + '<br>' : ''}${digits}자리 코드를 입력하세요. 코드는 서버에서만 검증됩니다.<br>틀려도 조사 횟수는 줄지 않습니다.</div>
       <div class="code-display" id="codeout"></div>
       <div class="keypad">
         ${[1, 2, 3, 4, 5, 6, 7, 8, 9]
           .map((n) => `<button data-k="${n}">${n}</button>`)
           .join('')}
         <button data-k="clear">C</button>
         <button data-k="0">0</button>
         <button data-k="ok">OK</button>
       </div>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-k]')) {
      b.onclick = () => this.key(b.dataset.k!);
    }
  }

  key(k: string) {
    if (this.el.sheet.dataset.kind !== 'keypad') return;
    const n = this.codeDigits || 4;
    if (k === 'clear') this.codeBuf = '';
    else if (k === 'ok') {
      if (this.codeBuf.length === n) {
        this.hooks.onCode(this.codeTarget, this.codeBuf);
        this.codeBuf = '';
      }
    } else if (this.codeBuf.length < n) this.codeBuf += k;
    const out = document.getElementById('codeout');
    if (out) out.textContent = this.codeBuf.padEnd(n, '·');
  }

  /**
   * **선택형 조사** (사흘 v8 A10 — photo_lab 프리셋). exam 을 마친 기계가 여는
   * 4지선다 — 고르면 곧바로 서버로 간다. 오답은 서버가 deny 로 문장만 돌려주고
   * 모달은 그대로 둔다(재시도). 정답이면 단서 카드가 열리며 이 모달을 덮는다.
   */
  openChoose(propId: string, title: string, prompt: string, options: string[]) {
    this.open(
      'choose',
      `<article class="choosecard">
         <h2>${escapeHtml(title)}</h2>
         <div class="meta">${escapeHtml(prompt)}<br>고르면 바로보낸다 — 틀려도 조사 횟수는 줄지 않는다.</div>
         <div class="chooseopts">
           ${options.map((o) => `<button data-opt="${escapeAttr(o)}">${escapeHtml(o)}</button>`).join('')}
         </div>
       </article>
       <div class="close-hint">${CLOSE_HINT}</div>`,
    );
    for (const b of this.el.sheet.querySelectorAll<HTMLElement>('[data-opt]')) {
      b.onclick = () => this.hooks.onChoose(propId, b.dataset.opt!);
    }
  }

  /** 지금 재생 중인 [R] 미디어 — 다음 재생이나 모달 닫힘 때 멈춘다 */
  private mediaEl: HTMLMediaElement | null = null;

  /**
   * **[R] 재생** (사흘 v8 A11) — 서버가 뿌린 play 신호에 기계의 음원/영상을 틴다.
   * 음원은 화면을 안 가리고 토스트만, 영상은 모달로. 다음 재생이 오면 앞의 것을
   * 멈춘다 — 두 사람이 연달아 틀면 마지막 것만 들린다.
   */
  playMedia(src: string, title: string) {
    this.mediaEl?.pause();
    this.mediaEl = null;
    if (src.endsWith('.mp4')) {
      this.open(
        'play',
        `<h2>${escapeHtml(title)}</h2>
         <video class="playvid" src="${escapeAttr(src)}" autoplay controls playsinline></video>
         <div class="close-hint">${CLOSE_HINT}</div>`,
      );
      this.mediaEl = this.el.sheet.querySelector('video');
      return;
    }
    const a = new Audio(src);
    this.mediaEl = a;
    a.play().catch(() => this.toast('재생이 막혔습니다 — 화면을 한 번 눌러 주세요.'));
    this.toast(`「${title}」 재생 중`);
  }
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function escapeAttr(s: string) {
  return escapeHtml(s);
}
